// src/components/pages/Auth/jwtExpiryWatcher.js

import {
  wpGetAuthSession,
  wpIsAuthSessionExpired,
  wpLogout,
} from "../../../api/wpAuth";

/*
 * Browsers cannot reliably schedule one setTimeout longer than
 * 2,147,483,647 milliseconds, which is about 24.85 days.
 *
 * A 31-day session therefore needs to be scheduled in smaller
 * segments and checked again after each segment.
 */
const MAX_TIMEOUT_MS = 2147483647;

const SESSION_STORAGE_KEYS = [
  "wp_jwt",
  "wp_refresh",
  "wp_user",
  "wp_session_id",
  "wp_session_expires_at",
  "wp_session_keep_login",
];

/**
 * Watch the fixed expiration deadline of the current browser session.
 *
 * Important behavior:
 * - Does not refresh the token before expiration.
 * - Does not extend the original 24-hour or 31-day deadline.
 * - Does not remove storage values directly.
 * - Uses wpLogout() as the single authoritative logout path.
 * - Supplies the scheduled session ID so an old timer cannot log out
 *   a newer login.
 */
export function startJwtExpiryWatcher({
  safetyIntervalMs = 15000,
  debug = false,
} = {}) {
  let timeoutId = null;
  let intervalId = null;

  const clearTimers = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    if (intervalId !== null) {
      clearInterval(intervalId);
    }

    timeoutId = null;
    intervalId = null;
  };

  const schedule = () => {
    clearTimers();

    const session =
      wpGetAuthSession();

    /*
     * No complete browser session means there is nothing for this
     * watcher to schedule.
     *
     * AuthContext will handle incomplete legacy authentication
     * storage during application startup.
     */
    if (!session) {
      return;
    }

    const scheduledSessionId =
      session.sessionId;

    const expireScheduledSession =
      () => {
        const currentSession =
          wpGetAuthSession();

        /*
         * The session was removed by manual logout.
         */
        if (!currentSession) {
          clearTimers();
          return;
        }

        /*
         * A newer login replaced the session that this timer was
         * originally created for.
         */
        if (
          currentSession.sessionId !==
          scheduledSessionId
        ) {
          schedule();
          return;
        }

        /*
         * The timeout may have fired after only one scheduling
         * segment of a 31-day session.
         */
        if (
          !wpIsAuthSessionExpired(
            currentSession,
          )
        ) {
          schedule();
          return;
        }

        clearTimers();

        const loggedOut =
          wpLogout({
            reason: "expired",

            message:
              "Session expired. Please login again.",

            sessionId:
              scheduledSessionId,
          });

        if (debug) {
          console.log(
            "[jwtExpiryWatcher] session expired",
            {
              sessionId:
                scheduledSessionId,

              loggedOut,
            },
          );
        }
      };

    if (
      wpIsAuthSessionExpired(session)
    ) {
      expireScheduledSession();
      return;
    }

    const millisecondsRemaining =
      session.expiresAtMs -
      Date.now();

    /*
     * Use segmented scheduling because a 31-day timeout exceeds
     * the maximum reliable setTimeout duration in browsers.
     */
    const timeoutDuration =
      Math.min(
        Math.max(
          millisecondsRemaining,
          0,
        ),
        MAX_TIMEOUT_MS,
      );

    timeoutId = setTimeout(
      expireScheduledSession,
      timeoutDuration,
    );

    /*
     * Safety check for:
     * - laptop sleep;
     * - suspended mobile browsers;
     * - background-tab timer throttling;
     * - system clock changes.
     */
    intervalId = setInterval(
      () => {
        const currentSession =
          wpGetAuthSession();

        if (!currentSession) {
          clearTimers();
          return;
        }

        if (
          currentSession.sessionId !==
          scheduledSessionId
        ) {
          schedule();
          return;
        }

        if (
          wpIsAuthSessionExpired(
            currentSession,
          )
        ) {
          expireScheduledSession();
        }
      },
      Math.max(
        1000,
        Number(safetyIntervalMs) ||
          15000,
      ),
    );
  };

  /*
   * Storage events run in other tabs using the same browser profile.
   * All tabs therefore reschedule when that browser logs in, logs
   * out, or receives a replacement token.
   */
  const handleStorage = (event) => {
    if (
      !event?.key ||
      SESSION_STORAGE_KEYS.includes(
        event.key,
      )
    ) {
      schedule();
    }
  };

  /*
   * Same-tab login, refresh, and logout events from wpAuth.js.
   */
  const handleAuthChanged = () => {
    schedule();
  };

  schedule();

  window.addEventListener(
    "storage",
    handleStorage,
  );

  window.addEventListener(
    "seho:wpAuthChanged",
    handleAuthChanged,
  );

  return function cleanup() {
    clearTimers();

    window.removeEventListener(
      "storage",
      handleStorage,
    );

    window.removeEventListener(
      "seho:wpAuthChanged",
      handleAuthChanged,
    );
  };
}